import classNames from 'classnames';
import { useState, useEffect } from 'react';
import { Heading } from '@acb_devel/design-system';
import { Text } from '@acb_devel/design-system';
import { getRegistrations } from '@services/registrations';
import { RegistrationStatus } from '@services/registrations';
import { RegistrationStatusSwitch } from '@components/assessors/molecules/RegistrationStatusSwitch/RegistrationStatusSwitch';
import { useModal } from '@acb_devel/design-system';
import { SelectAreasModal } from '@components/assessors/molecules/SelectAreasModal/SelectAreasModal';
import { updateRegistration } from '@services/registrations';
import { ToastRoot } from '@acb_devel/design-system';
import { useToastContext } from '@acb_devel/design-system';
import { Alert } from '@acb_devel/design-system';
import { Button } from '@acb_devel/design-system';
import { EditIcon } from '@acb_devel/design-system';
import { AngleDownIcon, AngleUpIcon } from '@acb_devel/design-system';

import type { User } from '@services/users';
import type { Registration } from '@services/registrations';

const MAX_VISIBLE_REGISTRATIONS = 4;

import styles from './RegistrationsCard.module.scss';

export function RegistrationsCard({
  className,
  user,
  registrations,
  setRegistrations,
}: {
  className?: string;
  user: User | undefined;
  registrations: Registration[];
  setRegistrations: (registrations: Registration[]) => void;
}) {
  const { isOpen, openModal, closeModal } = useModal();
  const { addToast } = useToastContext();

  const [selectedRegistration, setSelectedRegistration] =
    useState<Registration | null>(null);
  const [visibleRegistrations, setVisibleRegistrations] = useState<
    Registration[]
  >([]);

  useEffect(() => {
    if (!user) {
      return;
    }

    getRegistrations({
      userId: user.id,
      ordering: '-start_event',
    }).then(({ results }) => {
      setRegistrations(results);

      setVisibleRegistrations(results.slice(0, MAX_VISIBLE_REGISTRATIONS));
    });
  }, [user, setRegistrations]);

  function handleStatusChange(
    registration: Registration,
    status: RegistrationStatus
  ) {
    if (
      status === RegistrationStatus.CONFIRMED &&
      registration.event.eventAreas.length > 0
    ) {
      setSelectedRegistration(registration);
      openModal();
      return;
    }

    handleUpdateRegistration(registration, status);
  }

  function handleSelectedAreasSubmit(registration: Registration | null) {
    if (registration === null) {
      closeModal();
      return;
    }

    handleUpdateRegistration(registration, RegistrationStatus.CONFIRMED);
    closeModal();
  }

  function handleUpdateRegistration(
    registration: Registration,
    status: RegistrationStatus
  ) {
    if (registration.id === null) {
      return;
    }

    const updatedRegistrations = registrations.map((r) =>
      r.id === registration.id ? { ...r, ...registration, status } : r
    );

    updateRegistration({
      registrationId: registration.id,
      status,
      assignedEventAreas: registration.assignedEventAreas.map(
        (area) => area.id
      ),
    })
      .then(() => {
        setRegistrations(updatedRegistrations);
        setVisibleRegistrations((registrations) =>
          registrations.map((r) =>
            r.id === registration.id ? { ...r, ...registration, status } : r
          )
        );
      })
      .catch((error) => {
        addToast(
          <ToastRoot>
            <Alert
              variant="error"
              direction="column"
              title={`Error al actualizar la inscripción`}
              text="Ha ocurrido un error al actualizar la inscripción. Por favor, inténtalo de nuevo."
            />
          </ToastRoot>
        );
        console.error(error);
      });
  }

  if (registrations.length === 0) {
    return null;
  }

  return (
    <div className={classNames(styles.card, className)}>
      <Heading as="h2" variant="heading-1">
        Inscripciones
      </Heading>
      <div className={styles.card__registrations}>
        {visibleRegistrations.map((registration) => (
          <div key={registration.id} className={styles.card__registration}>
            <div className={styles.card__registrationHeader}>
              <div className={styles.card__registrationInfo}>
                <Text
                  variant="body-1"
                  className={styles.card__registrationText}
                  as="p"
                >
                  {registration.event.fullName}
                </Text>
              </div>
              <RegistrationStatusSwitch
                registration={registration}
                onStatusChange={handleStatusChange}
              />
            </div>
            {registration.status === RegistrationStatus.CONFIRMED && (
              <>
                <div className={styles.card__registrationAreas}>
                  <div>
                    <Text variant="caption-medium" as="p">
                      Áreas asignadas
                    </Text>
                    {registration.assignedEventAreas.length > 0 && (
                      <ul className={styles.card__registrationAreasList}>
                        {registration.assignedEventAreas.map((area) => (
                          <li key={area.id}>
                            <Text
                              key={area.id}
                              variant="body-2"
                              className={styles.card__registrationArea}
                              as="p"
                            >
                              {area.name}
                            </Text>
                          </li>
                        ))}
                      </ul>
                    )}
                    {registration.assignedEventAreas.length === 0 && (
                      <Text variant="body-2" as="p">
                        Sin áreas asignadas
                      </Text>
                    )}
                  </div>
                  {registration.event.eventAreas.length > 0 && (
                    <Button
                      variant="outline"
                      color="accent"
                      size="md"
                      isOnlyIcon={true}
                      onClick={() => {
                        setSelectedRegistration(registration);
                        openModal();
                      }}
                    >
                      <EditIcon />
                    </Button>
                  )}
                </div>
              </>
            )}
            {registration.status !== RegistrationStatus.CONFIRMED && (
              <>
                <div className={styles.card__registrationAreas}>
                  <div>
                    <Text variant="caption-medium" as="p">
                      Preferencias del usuario
                    </Text>
                    <ul className={styles.card__registrationAreasList}>
                      {registration.preferredEventAreas.map((area) => (
                        <li key={area.id}>
                          <Text variant="body-2" as="p">
                            {area.name}
                          </Text>
                        </li>
                      ))}
                    </ul>
                    {registration.preferredEventAreas.length === 0 && (
                      <Text variant="body-2" as="p">
                        Sin preferencias
                      </Text>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
        {registrations.length > visibleRegistrations.length && (
          <div className={styles.card__registrationsFooter}>
            <Button
              variant="flat"
              color="accent"
              size="md"
              upperCase={false}
              onClick={() => setVisibleRegistrations(registrations)}
            >
              Ver todas las inscripciones (
              {registrations.length - visibleRegistrations.length} más){' '}
              <AngleDownIcon />
            </Button>
          </div>
        )}
        {registrations.length === visibleRegistrations.length &&
          registrations.length > MAX_VISIBLE_REGISTRATIONS && (
            <div className={styles.card__registrationsFooter}>
              <Button
                variant="flat"
                color="accent"
                size="md"
                upperCase={false}
                onClick={() =>
                  setVisibleRegistrations(
                    registrations.slice(0, MAX_VISIBLE_REGISTRATIONS)
                  )
                }
              >
                Ver menos inscripciones <AngleUpIcon />
              </Button>
            </div>
          )}
      </div>
      <SelectAreasModal
        isOpen={isOpen}
        closeModal={closeModal}
        registration={selectedRegistration}
        setRegistration={setSelectedRegistration}
        onSubmitted={handleSelectedAreasSubmit}
      />
    </div>
  );
}
